import { useEffect } from "react";
import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import { connect } from "react-redux";
import { useState } from "react";
import {
  getClinicalSummaryRequest,
  getVikrutiAverageData,
  getSymptomsDataRequest,
  getScoreLabelRequest,
} from "../../../PatientApiActions";
import { Button, Chip } from "@material-ui/core";
import clsx from "clsx";
import LabFindings from "./LabFindings";
import VisibilityRoundedIcon from "@material-ui/icons/VisibilityRounded";
import VisibilityOffRoundedIcon from "@material-ui/icons/VisibilityOffRounded";
import { setClinicalSummary } from "../../../PatientActions";
import moment from "moment";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import useCommonStyles from "./style";

const ClinicalSummary = ({
  patientId,
  summary,
  getClinicalSummary,
  updateClinicalSummary,
  getVikrutiAverageData,
  vikrutiAverageData,
  getSymptomsData,
  symptomsData,
  getScoreLabel,
  selectedValues = {},
  labFindings,
}) => {
  const classes = useCommonStyles();
  const [timeFilter, setTimeFilter] = useState("Weekly");
  // Which dataset the wide card shows: "vikruti" (readings chart) or "risk"
  // (lab findings). Only meaningful when the patient has both.
  const [panel, setPanel] = useState("vikruti");
  const [vikrutiAvailable, setVikrutiAvailable] = useState(false);
  const [prakriti, setPrakriti] = useState("-");
  const [visibleLines, setVisibleLines] = useState({
    Vata: true,
    Kapha: true,
    Pitta: true,
  });
  useEffect(() => {
    if (patientId) {
      getClinicalSummary(patientId);
      getSymptomsData(patientId);
    }
  }, [patientId]);

  // Prakriti comes from the mad-medicine score_labels API (not the analytics
  // endpoint that feeds the other parameters).
  useEffect(() => {
    let active = true;
    if (patientId) {
      getScoreLabel(patientId, "Prakriti")
        .then((label) => {
          if (active) setPrakriti(label || "-");
        })
        .catch(() => {
          if (active) setPrakriti("-");
        });
    }
    return () => {
      active = false;
    };
  }, [patientId]);

  useEffect(() => {
    updateClinicalSummary([]);
  }, []);

  useEffect(() => {
    if (patientId) {
      const apiType = mapTimeFilterToApiType[timeFilter];
      getVikrutiAverageData(patientId, apiType);
    }
  }, [patientId, timeFilter]);

  const toggleLine = (lineKey) => {
    setVisibleLines((prev) => ({
      ...prev,
      [lineKey]: !prev[lineKey],
    }));
  };
  // Value for an assessment parameter: doctor-selected value (Assessment tab)
  // takes priority, else the backend analytics Inference, else "-".
  const valueFor = (paramKey, questionId) => {
    const selected = selectedValues[paramKey];
    if (selected) return selected;
    const match = summary.find((item) => item.questionId === questionId);
    return (match && match.Inference) || "-";
  };

  const chipConfigs = [
    {
      key: "Vata",
      label: "Vata",
      color: "#a78bfa",
      backgroundColor: "#f3f0ff",
    },
    {
      key: "Pitta",
      label: "Pitta",
      color: "#fca5a5",
      backgroundColor: "#fef2f2",
    },
    {
      key: "Kapha",
      label: "Kapha",
      color: "#6ee7b7",
      backgroundColor: "#f0fdf4",
    },
  ];

  const mapTimeFilterToApiType = {
    Daily: "day",
    Weekly: "week",
    Monthly: "month",
  };

  const getChartData = () => {
    const data = vikrutiAverageData?.data;
    const metadata = vikrutiAverageData?.metadata;

    if (!data || !metadata?.timeRange || !data.sessions) return [];

    const { dayType } = metadata.timeRange;

    return data.sessions.map((sessionTime, i) => {
      const pointTime = moment(sessionTime);

      let label = "";
      if (dayType === "day") {
        label = pointTime.format("HH:mm");
      } else if (dayType === "week") {
        label = pointTime.format("ddd, DD MMM");
      } else if (dayType === "month") {
        label = pointTime.format("DD MMM");
      }

      return {
        date: label,
        Vata: data.vat_avg?.[i] ?? 0,
        Pitta: data.pit_avg?.[i] ?? 0,
        Kapha: data.kaf_avg?.[i] ?? 0,
      };
    });
  };

  const chartData = getChartData();
  const hasLabFindings = Array.isArray(labFindings) && labFindings.length > 0;

  // A patient with no readings must not be shown an empty chart: the slot
  // carries the Risk Flags instead. Availability is sticky for the patient so
  // that picking a range with no sessions in it (e.g. Daily) does not swap the
  // panel out mid-look and take the range dropdown with it.
  useEffect(() => {
    setVikrutiAvailable(false);
    setPanel("vikruti");
  }, [patientId]);

  useEffect(() => {
    if (chartData.length > 0) setVikrutiAvailable(true);
  }, [chartData.length]);

  const showPanelToggle = vikrutiAvailable && hasLabFindings;
  // Whatever the doctor picked, but never the chart when there is nothing to
  // plot: without readings the slot belongs to the Risk Flags.
  const activePanel = showPanelToggle
    ? panel
    : vikrutiAvailable
    ? "vikruti"
    : "risk";

  // The 5 diagnostic parameters shown in the Overview summary.
  //  - Prakriti: mad-medicine score_labels API
  //  - Dosha-Vikriti: not wired yet (kept as "-")
  //  - Agni / Ama / Ojas: reuse the existing assessment data
  const diagnosticParams = [
    { key: "prakriti", icon: "🧬", label: "Prakriti", value: prakriti || "-" },
    { key: "vikriti", icon: "🚫", label: "Dosha-Vikriti", value: "-" },
    { key: "agni", icon: "🔥", label: "Agni", value: valueFor("agni", "Eira_Clinic_7") },
    { key: "ama", icon: "🟢", label: "Ama", value: valueFor("ama", "Eira_Clinic_20") },
    { key: "ojas", icon: "✨", label: "Ojas", value: valueFor("ojas", "PR_23") },
  ];

  const getDoshaChipClass = (dosha) => {
    if (!dosha) return classes.doshaChip;
    const doshaLower = dosha.toLowerCase();
    if (doshaLower.includes("vata"))
      return `${classes.doshaChip} ${classes.vataChip}`;
    if (doshaLower.includes("pitta"))
      return `${classes.doshaChip} ${classes.pittaChip}`;
    if (doshaLower.includes("kapha"))
      return `${classes.doshaChip} ${classes.kaphaChip}`;
    return classes.doshaChip;
  };

  const strotaColors = {
    pranvaha: "#A1E2DA",
    udakavaha: "#79C2D0",
    annavaha: "#E2994A",
    rasavaha: "#E7A7B7",
    raktavaha: "#C0392B",
    mamsavaha: "#B35C44",
    medovaha: "#F4C15D",
    asthivaha: "#A89F91",
    majjavaha: "#5C4B8B",
    sukravaha: "#B69ACF",
    mutravaha: "#A4D792",
    purishvaha: "#8C6A4D",
    svedavaha: "#F7B89C",
    artavavaha: "#D16C85",
    manovaha: "#8A8CCF",
  };

  const getStrotaColor = (strota) => {
    if (!strota) return "#000000";
    const normalized = strota.trim().toLowerCase();
    return strotaColors[normalized] || "#000000";
  };

  const symptomsArray = symptomsData?.[0]?.[0]?.symptoms || [];

  return (
    <Box>
      <Typography className={classes.sectionTitle}>Clinical Summary</Typography>

      <table className={classes.symptomTable}>
        <thead>
          <tr>
            <th className={classes.tableHeader}>Symptoms</th>
            <th className={classes.tableHeader}>Dosha</th>
            <th className={classes.tableHeader}>Strotas</th>
          </tr>
        </thead>
        <tbody>
          {(symptomsArray || []).map((item, index) => (
            <tr key={index}>
              <td className={classes.tableCell}>
                <div className={classes.symptomRow}>
                  <div className={classes.symptoms}>
                    {Array.isArray(item.question_values) &&
                    item.question_values.length
                      ? item.question_values.join(", ")
                      : item.question_values || "No symptoms"}{" "}
                  </div>
                </div>
              </td>

              <td className={classes.tableCell}>
                <span className={getDoshaChipClass(item.dosha)}>
                  {item.dosha || "-"}
                </span>
              </td>

              <td className={classes.tableCell}>
                <span>
                  {Array.isArray(item.Strotas)
                    ? item.Strotas.map((strota, idx) => {
                        const color = getStrotaColor(strota);
                        return (
                          <span
                            key={idx}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              marginRight: 8,
                            }}
                          >
                            <span
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: color,
                                display: "inline-block",
                                marginRight: 4,
                              }}
                            />
                            <span style={{ color: color }}>
                              {strota.trim()}
                            </span>
                            {idx < item.Strotas.length - 1 ? "," : ""}
                          </span>
                        );
                      })
                    : item.Strotas || "-"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Box className={classes.vikrutiSection}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card className={classes.assessmentCard}>
              <Typography className={classes.sectionTitle}>
                Ayurvedic Diagnostic Parameters
              </Typography>

              <Grid container spacing={2}>
                {diagnosticParams.map((item) => (
                  <Grid item xs={6} key={item.key}>
                    <Box className={classes.assessmentItem}>
                      <Typography className={classes.assessmentLabel}>
                        {item.icon} {item.label}
                      </Typography>
                      <Typography className={classes.assessmentValue}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card className={classes.chartCard}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                style={{ gap: 8 }}
              >
                {showPanelToggle ? (
                  <Box className={classes.panelToggle}>
                    <Button
                      disableElevation
                      className={clsx(
                        classes.panelToggleBtn,
                        activePanel === "vikruti" && classes.panelToggleBtnActive
                      )}
                      onClick={() => setPanel("vikruti")}
                    >
                      Vikruti Analysis
                    </Button>
                    <Button
                      disableElevation
                      className={clsx(
                        classes.panelToggleBtn,
                        activePanel === "risk" && classes.panelToggleBtnActive
                      )}
                      onClick={() => setPanel("risk")}
                    >
                      Risk Flags
                    </Button>
                  </Box>
                ) : (
                  <Typography className={classes.sectionTitle}>
                    {activePanel === "risk"
                      ? "Key Risk Flags & Lab Indicators (AI Highlighted)"
                      : "Vikruti Analysis"}
                  </Typography>
                )}

                {activePanel === "vikruti" && (
                  <FormControl
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 100 }}
                  >
                    <Select
                      value={timeFilter}
                      onChange={(e) => setTimeFilter(e.target.value)}
                      className={classes.dayType}
                    >
                      <MenuItem value="Daily">Daily</MenuItem>
                      <MenuItem value="Weekly">Weekly</MenuItem>
                      <MenuItem value="Monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>

              {activePanel === "risk" ? (
                <Box className={classes.riskPanel}>
                  <LabFindings findings={labFindings} />
                </Box>
              ) : vikrutiAverageData?.data &&
              Object.values(vikrutiAverageData.data).every(
                (arr) => Array.isArray(arr) && arr.length === 0
              ) ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height={280}
                >
                  <Typography color="textSecondary">
                    {vikrutiAverageData.message || "No data available"}
                  </Typography>
                </Box>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart
                      data={chartData}
                      margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="colorVata"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#a78bfa"
                            stopOpacity={0.6}
                          />
                          <stop
                            offset="95%"
                            stopColor="#a78bfa"
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient
                          id="colorKapha"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#6ee7b7"
                            stopOpacity={0.6}
                          />
                          <stop
                            offset="95%"
                            stopColor="#6ee7b7"
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient
                          id="colorPitta"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#fca5a5"
                            stopOpacity={0.6}
                          />
                          <stop
                            offset="95%"
                            stopColor="#fca5a5"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>

                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12, fill: "#666" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis hide />

                      <Tooltip
                        contentStyle={{
                          borderRadius: 12,
                          backgroundColor: "#fff",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                      />
                      {visibleLines.Vata && (
                        <Area
                          type="monotone"
                          dataKey="Vata"
                          stroke="#a78bfa"
                          fillOpacity={1}
                          fill="url(#colorVata)"
                          strokeWidth={2}
                          dot={{
                            stroke: "#a78bfa",
                            strokeWidth: 2,
                            r: 4,
                            fill: "#fff",
                          }}
                          activeDot={{ r: 6 }}
                          stackId="1"
                        />
                      )}

                      {visibleLines.Kapha && (
                        <Area
                          type="monotone"
                          dataKey="Kapha"
                          stroke="#6ee7b7"
                          fillOpacity={1}
                          fill="url(#colorKapha)"
                          strokeWidth={2}
                          dot={{
                            stroke: "#6ee7b7",
                            strokeWidth: 2,
                            r: 4,
                            fill: "#fff",
                          }}
                          activeDot={{ r: 6 }}
                          stackId="1"
                        />
                      )}
                      {visibleLines.Pitta && (
                        <Area
                          type="monotone"
                          dataKey="Pitta"
                          stroke="#fca5a5"
                          fillOpacity={1}
                          fill="url(#colorPitta)"
                          strokeWidth={2}
                          dot={{
                            stroke: "#fca5a5",
                            strokeWidth: 2,
                            r: 4,
                            fill: "#fff",
                          }}
                          activeDot={{ r: 6 }}
                          stackId="1"
                        />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>

                  <Typography
                    style={{
                      textAlign: "center",
                      fontSize: "0.72rem",
                      color: "#9aa4b2",
                      marginTop: 12,
                    }}
                  >
                    Tap a dosha to show or hide it on the chart
                  </Typography>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={4}
                    mt={1}
                    mb={1}
                    flexWrap="wrap"
                  >
                    {chipConfigs.map((config) => {
                      const on = visibleLines[config.key];
                      return (
                        <Chip
                          key={config.key}
                          label={config.label}
                          clickable
                          onClick={() => toggleLine(config.key)}
                          icon={
                            on ? (
                              <VisibilityRoundedIcon
                                style={{ color: "#fff", fontSize: 16 }}
                              />
                            ) : (
                              <VisibilityOffRoundedIcon
                                style={{ color: config.color, fontSize: 16 }}
                              />
                            )
                          }
                          style={{
                            backgroundColor: on
                              ? config.color
                              : config.backgroundColor,
                            color: on ? "#fff" : config.color,
                            border: `1px solid ${config.color}`,
                            fontWeight: 600,
                            transition: "all 0.2s ease-in-out",
                            borderRadius: "16px",
                            minWidth: "96px",
                            height: "34px",
                            cursor: "pointer",
                            opacity: on ? 1 : 0.7,
                            margin: "4px 12px",
                          }}
                          variant={on ? "default" : "outlined"}
                        />
                      );
                    })}
                  </Box>
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    summary: state.athletes.clinicalSummary,
    vikrutiAverageData: state.athletes.vikrutiAverageData,
    symptomsData: state.athletes.symptomsData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getClinicalSummary: (id) => {
      return dispatch(getClinicalSummaryRequest(id)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    updateClinicalSummary: (data) => {
      return dispatch(setClinicalSummary(data));
    },
    getVikrutiAverageData: (userId, type) => {
      return dispatch(getVikrutiAverageData(userId, type)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getSymptomsData: (id) => {
      return dispatch(getSymptomsDataRequest(id)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getScoreLabel: (mobileNumber, scoreName) => {
      return dispatch(getScoreLabelRequest(mobileNumber, scoreName)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ClinicalSummary);
